/**
 * The leadership summary (release 8, C6; capability row 67): one screen for a
 * board member or a CISO, composed from numbers the product already computes.
 *
 * It is NOT a page or a tab. A separate "Executive briefing" tab was built and
 * reverted on 23 Jun 2026 (15f9d5fd, 7d1d6cbe): the founder wanted the DSPM
 * picture in the board-report area and in front of the people who read for the
 * board. So this block sits (1) on the Home of the DPO and the Auditor, BELOW
 * the role's own action tiles (what needs doing stays on the first screen), and
 * (2) in the board-report area of Compliance > Regulatory Coverage, beside the
 * evidence packs.
 *
 * Shape (UX rulebook: at most six tiles on a Home):
 *   - six tiles: the five the Regulatory Coverage card always had (risk posture,
 *     critical open, high open, classified, children's data) and the 30-day trend;
 *   - ONE "Needs attention" list for everything else: a broken audit trail,
 *     breaches not yet notified, encrypted files not read, the worst stores, and
 *     (the largest holding of personal data is a plain line below the list);
 *   - one line saying what could not be checked, and why.
 *
 * Every figure says when it could not be worked out instead of showing a zero:
 * "not checked" is not "nothing found". A source the caller may not have is named
 * in the "could not be checked" line; it is not left out, because a missing line
 * would read as a clean one.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ScrollText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getDiscoveryBoardSummary, type DiscoveryBoardSummary } from '@/lib/api';
import { getLeadershipSummary, type Block, type LeadershipSummary } from '@/lib/leadership-api';
import { pct, points } from '@/pages/discovery/StoreDensityCard';

const INDIAN = new Intl.NumberFormat('en-IN');
const n = (v: number): string => INDIAN.format(v);

export type Tone = 'neutral' | 'attention' | 'good';

export interface Tile {
  id: string;
  label: string;
  value: string;
  detail: string;
  tone: Tone;
  to?: string;
}

export interface AttentionItem {
  id: string;
  text: string;
  tone: Tone;
  to?: string;
}

const REASON: Record<'not_permitted' | 'no_history' | 'not_yet_run' | 'unavailable', string> = {
  not_permitted: 'not shown to your role',
  no_history: 'only one day recorded so far',
  not_yet_run: 'not recorded yet',
  unavailable: 'could not be read just now',
};

function reasonOf<T>(b: Block<T> | undefined): string {
  return b === undefined ? 'not loaded' : b.available ? '' : REASON[b.reason];
}

/** "3 h ago", "2 days ago". */
export function since(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'at an unknown time';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 90) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

const NA = (id: string, label: string, detail: string): Tile => ({ id, label, value: 'Not available', detail, tone: 'neutral' });

/** The six tiles, from the two answers. Pure, so every wording is tested without a browser. */
export function leadershipTiles(board: DiscoveryBoardSummary | null, lead: LeadershipSummary | null): Tile[] {
  const none = 'No scan has produced this yet.';
  const t: Tile[] = [];

  // The five posture tiles the Regulatory Coverage card always had, with their meaning.
  t.push(
    board
      ? {
          id: 'posture',
          label: 'Risk posture',
          value: `${board.posture.score}/100`,
          detail: `${board.posture.band} band. Higher is worse.`,
          tone: board.posture.band === 'critical' || board.posture.band === 'high' ? 'attention' : 'neutral',
          to: '/dspm/inventory',
        }
      : NA('posture', 'Risk posture', none),
  );
  t.push(
    board
      ? { id: 'critical', label: 'Critical open', value: n(board.exposures.criticalOpen), detail: 'exposures', tone: board.exposures.criticalOpen > 0 ? 'attention' : 'neutral', to: '/dspm/findings' }
      : NA('critical', 'Critical open', none),
  );
  t.push(
    board
      ? { id: 'high', label: 'High open', value: n(board.exposures.highOpen), detail: 'exposures', tone: 'neutral', to: '/dspm/findings' }
      : NA('high', 'High open', none),
  );
  t.push(
    board
      ? {
          id: 'classified',
          label: 'Classified',
          value: `${Math.round(board.classification.classifiedPct * 100)}%`,
          detail: `${n(board.classification.classifiedAssets)}/${n(board.classification.totalAssets)} assets`,
          tone: 'neutral',
          to: '/dspm/inventory',
        }
      : NA('classified', 'Classified', none),
  );
  t.push(
    board
      ? {
          id: 'children',
          label: "Children's data (s.9)",
          value: board.childrensData.status === 'detected' ? n(board.childrensData.minorRecordsDetected) : 'None',
          detail: board.childrensData.status === 'detected' ? 'records detected' : 'none detected',
          tone: board.childrensData.status === 'detected' ? 'attention' : 'neutral',
          to: '/dspm/section-9',
        }
      : NA("children", "Children's data (s.9)", none),
  );

  // The sixth: is exposure going down?
  const tr = lead?.riskTrend;
  if (tr?.available) {
    const d = tr.data;
    const word = d.openChange === 0 ? 'unchanged' : d.openChange < 0 ? `down ${n(-d.openChange)}` : `up ${n(d.openChange)}`;
    t.push({
      id: 'trend',
      label: 'Unresolved, 30 days',
      value: n(d.openNow),
      detail: `${word} from ${n(d.openThen)} on ${d.fromDay}`,
      tone: d.openChange < 0 ? 'good' : d.openChange > 0 ? 'attention' : 'neutral',
      to: '/dspm/findings',
    });
  } else {
    t.push(NA('trend', 'Unresolved, 30 days', `${reasonOf(tr)}.`.replace(/^./, (c) => c.toUpperCase())));
  }
  return t;
}

/**
 * The one "Needs attention" list, and the sources that could not be checked.
 * Order: what is broken or overdue first, then what is unread, then where the
 * worst stores are. The largest holding of personal data is returned apart
 * (`holding`): it is information, not something to attend to.
 */
export function attention(
  board: DiscoveryBoardSummary | null,
  lead: LeadershipSummary | null,
  now: number = Date.now(),
): { items: AttentionItem[]; holding: AttentionItem | null; unchecked: string[] } {
  const items: AttentionItem[] = [];
  let holding: AttentionItem | null = null;
  const unchecked: string[] = [];

  const a = lead?.auditChain;
  if (a?.available) {
    if (a.data.status === 'broken') {
      items.push({
        id: 'audit',
        text: `The audit trail is broken at block ${a.data.brokenAtBlockIndex ?? 'unknown'} (found ${since(a.data.verifiedAt, now)}). Evidence from it cannot be relied on until it is looked at.`,
        tone: 'attention',
        to: '/admin/audit-logs',
      });
    }
  } else unchecked.push(`audit trail (${reasonOf(a)})`);

  const b = lead?.breaches;
  if (b?.available) {
    if (b.data.notYetNotified > 0) {
      items.push({
        id: 'breaches',
        text: `${n(b.data.notYetNotified)} breach${b.data.notYetNotified === 1 ? '' : 'es'} not yet notified (${n(b.data.draft)} draft, ${n(b.data.acknowledged)} acknowledged). The notification clock is running.`,
        tone: 'attention',
        to: '/compliance/breaches',
      });
    }
  } else unchecked.push(`breaches (${reasonOf(b)})`);

  const u = lead?.unreadable;
  if (u?.available) {
    const d = u.data;
    if (d.storesReported === 0) unchecked.push('encrypted files (no store has reported a scan yet)');
    else if (d.storesSeparatingEncrypted === 0) unchecked.push('encrypted files (the agents reporting cannot separate them yet)');
    else {
      const blind = d.storesReported - d.storesSeparatingEncrypted;
      if (d.encryptedFiles > 0) {
        items.push({
          id: 'unreadable',
          text: `${n(d.encryptedFiles)} encrypted file${d.encryptedFiles === 1 ? '' : 's'} not read, in ${n(d.storesWithEncrypted)} store${d.storesWithEncrypted === 1 ? '' : 's'}. They may hold personal data that is not in the results${blind > 0 ? `; ${n(blind)} other store${blind === 1 ? '' : 's'} cannot say (older agent)` : ''}.`,
          tone: 'attention',
          to: '/dspm/scans',
        });
      } else if (blind > 0) {
        unchecked.push(`encrypted files in ${n(blind)} store${blind === 1 ? '' : 's'} (older agent cannot say)`);
      }
    }
  } else unchecked.push(`encrypted files (${reasonOf(u)})`);

  if (board) {
    for (const r of board.exposures.topRisks.slice(0, 3)) {
      items.push({
        id: `risk-${r.assetRef}`,
        text: `${r.name} (${r.sourceType}): risk score ${r.score}, ${n(r.critical)} critical and ${n(r.high)} high open${r.orphan ? ', no owner' : ''}.`,
        tone: r.band === 'critical' || r.band === 'high' ? 'attention' : 'neutral',
        to: '/dspm/inventory',
      });
    }
  }

  const s = lead?.densest;
  if (s?.available) {
    const top = s.data.stores[0];
    if (top) {
      // Information, not attention: it is shown as a plain line below the list, never in it.
      const size = top.dataPoints === null ? 'row counts unknown' : `about ${points(top.dataPoints)} data points (rows x personal columns)`;
      holding = {
        id: 'densest',
        text: `Largest holding of personal data: ${top.name} (${top.sourceType}), ${size}; ${top.personalColumns} of ${top.columnsKnown} columns, ${pct(top.density)}, hold it.${s.data.unmeasuredStores > 0 ? ` ${n(s.data.unmeasuredStores)} store${s.data.unmeasuredStores === 1 ? '' : 's'} with personal data cannot be measured.` : ''}`,
        tone: 'neutral',
        to: '/dspm/inventory',
      };
    }
  } else unchecked.push(`personal-data holdings (${reasonOf(s)})`);

  return { items, holding, unchecked };
}

const TONE_VALUE: Record<Tone, string> = {
  neutral: 'text-foreground',
  attention: 'text-sev-high',
  good: 'text-success',
};

function TileView({ t }: { t: Tile }) {
  const body = (
    <>
      <div className="text-xs text-muted-foreground">{t.label}</div>
      <div className={`mt-0.5 text-xl font-semibold truncate ${TONE_VALUE[t.tone]}`} title={t.value}>
        {t.value}
      </div>
      <div className="text-xs text-muted-foreground">{t.detail}</div>
    </>
  );
  return t.to ? (
    <Link
      to={t.to}
      data-tile={t.id}
      className="block rounded-md border border-border/40 p-3 hover:bg-muted/40 focus-visible:outline focus-visible:outline-2"
    >
      {body}
    </Link>
  ) : (
    <div data-tile={t.id} className="rounded-md border border-border/40 p-3">
      {body}
    </div>
  );
}

export function LeadershipSummaryView({
  board,
  lead,
  boardReportLink = true,
}: {
  board: DiscoveryBoardSummary | null;
  lead: LeadershipSummary | null;
  /** The Home links to the board-report area; that area is already there, so it passes false. */
  boardReportLink?: boolean;
}) {
  const tiles = leadershipTiles(board, lead);
  const { items, holding, unchecked } = attention(board, lead);
  return (
    <GlassCard className="p-4" data-testid="leadership-summary" aria-label="Leadership summary">
      <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <ScrollText className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> Leadership summary
          </CardTitle>
          <CardDescription className="text-xs">
            Where we stand, what changed in 30 days, and what needs attention. "Not available" means it could not be worked out, not that it is zero.
          </CardDescription>
        </div>
        {boardReportLink && (
          <Button size="sm" asChild>
            <Link to="/compliance/regulatory-coverage">
              Board report <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </CardHeader>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <TileView key={t.id} t={t} />
        ))}
      </div>
      <div className="mt-3" data-testid="leadership-attention">
        <div className="text-sm font-medium mb-1">Needs attention</div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing needs attention in what was checked.</p>
        ) : (
          <ol className="list-decimal pl-5 space-y-0.5 text-sm text-foreground/90">
            {items.map((x) => (
              <li key={x.id} data-attention={x.id}>
                {x.to ? (
                  <Link to={x.to} className="hover:underline">
                    {x.text}
                  </Link>
                ) : (
                  x.text
                )}
              </li>
            ))}
          </ol>
        )}
        {holding && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="leadership-holding">
            {holding.to ? (
              <Link to={holding.to} className="hover:underline">
                {holding.text}
              </Link>
            ) : (
              holding.text
            )}
          </p>
        )}
        {unchecked.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="leadership-unchecked">
            Could not be checked: {unchecked.join('; ')}.
          </p>
        )}
      </div>
    </GlassCard>
  );
}

/** Loads both answers; a failure of either leaves what it fed as "Not available". */
export function useLeadershipSummary() {
  const [state, setState] = useState<{ board: DiscoveryBoardSummary | null; lead: LeadershipSummary | null; loading: boolean }>({
    board: null,
    lead: null,
    loading: true,
  });
  const load = useCallback(() => {
    let live = true;
    Promise.all([getDiscoveryBoardSummary().catch(() => null), getLeadershipSummary().catch(() => null)]).then(([board, lead]) => {
      if (live) setState({ board, lead, loading: false });
    });
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => load(), [load]);
  return state;
}

/** The block as the Home mounts it. */
export function LeadershipSummary({ boardReportLink = true }: { boardReportLink?: boolean } = {}) {
  const { board, lead, loading } = useLeadershipSummary();
  if (loading) {
    return (
      <GlassCard className="p-4" data-testid="leadership-summary-loading">
        <p className="text-sm text-muted-foreground">Reading the leadership summary...</p>
      </GlassCard>
    );
  }
  if (board === null && lead === null) {
    return (
      <GlassCard className="p-4" data-testid="leadership-summary-unavailable">
        <p className="text-sm text-muted-foreground">The leadership summary could not be loaded just now. The tiles above are unaffected.</p>
      </GlassCard>
    );
  }
  return <LeadershipSummaryView board={board} lead={lead} boardReportLink={boardReportLink} />;
}
